import store from '@state/store'
import Vue from 'vue'
import VueRouter from 'vue-router'
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta'
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress/nprogress'
import { hasAccessToRoute } from '@/helpers/permissions-helpers.js'
import ErrorPageCodes from '@/shared/constants/error/ErrorPageCodes'
import { getLanguageBasedOnBaseURL } from '@/helpers/language-helpers'
import RequestErrorSource from '@/shared/constants/error/RequestErrorSource'
import { decideRouteBasedOnFeatureToggles } from '@/services/features/featureDecisions.js'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import routes from './routes'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'metaInfo',
})

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

const startRouteLoading = () => {
  // Begin loading animation. Only really required for initial page loads/refreshes.
  if (!store.getters.hasLoadedAppOnce) {
    store.dispatch('startLoadingApp')
  }

  // Only display the top loading bar after initial load
  if (store.getters.hasLoadedAppOnce) NProgress.start()
}
const stopRouteLoading = () => {
  // Prevents full page loader showing up on every route change
  if (store.getters.isLoadingApp) {
    store.dispatch('setAppAsLoaded')
  }

  // Complete the full page loading animation
  store.dispatch('finishLoadingApp')
  NProgress.done()
}

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  // Check if auth is required on this route
  // (including nested routes).
  const isPublic = routeTo.matched.some((route) => route.meta.public)

  // If auth isn't required for the route, just continue.
  if (isPublic) return next()

  startRouteLoading()

  // If auth is required and the user isn't logged in...
  if (!store.getters['auth/isUserLoggedIn']) {
    // Retrieve another access token...
    try {
      const validToken = await store.dispatch('auth/refreshToken')

      // Then continue if the token is valid & was acquired successfully,
      // otherwise redirect to login.
      if (!validToken.isSuccess || !store.getters['auth/isUserLoggedIn']) {
        throw new Error('Could not refresh access token')
      }
    } catch {
      return redirectToLandingPage()
    }
  }

  // Get user's profile if not already set or isn't fresh
  if (
    !store.getters['auth/currentUser'] ||
    !store.getters['auth/permissions'] ||
    store.getters['auth/permissions'].length === 0
  ) {
    try {
      // throw new Error()
      const loadUserProfileResult = await store.dispatch(
        'auth/getCurrentUserProfile'
      )

      if (!loadUserProfileResult.isSuccess)
        return getErrorPageRedirectByStatusCode(loadUserProfileResult)

      // Set locale loaded in with profile
      await store.dispatch('setLocale', loadUserProfileResult.data.language)
    } catch (ex) {
      return redirectToErrorPage(ErrorPageCodes.ACCOUNT_LOAD_FAILURE.routeName)
    }
  }

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  if (
    !store.getters['auth/isUserLoggedIn'] ||
    !store.getters['auth/currentUser']
  )
    redirectToLandingPage()

  // If a client hasn't been selected, handle default redirection logic
  // based on permissions
  const overviewPages = [
    routeDefinitions.clientOverview.path,
    routeDefinitions.clientGroupOverview.path,
  ]

  if (
    !store.getters['client/clientDetails'] &&
    !overviewPages.includes(routeTo.path)
  ) {
    if (store.getters['auth/redirectToClientOverview'])
      await redirectToClientOverview()
    else {
      const result = await store.dispatch(
        'client/setClient',
        store.getters['auth/firstAvailableClient'].clientId
      )

      if (!result.isSuccess) return redirectToErrorPage()
    }
  }

  // Load the contact's rep if they aren't loaded yet
  if (
    !store.getters['client/owner'](store.getters['client/selectedClientId'])
  ) {
    store.dispatch(
      'client/loadClientAdditionalInformation',
      store.getters['client/selectedClientId']
    )
  }

  // Check if permissions are required for route
  if (!hasAccessToRoute(routeTo, store))
    redirectToErrorPage(ErrorPageCodes.FORBIDDEN.routeName)

  return decideRouteBasedOnFeatureToggles(
    store.getters['features/featureToggles'],
    routeTo,
    next
  )

  function redirectToLandingPage() {
    stopRouteLoading()

    // To complete the redirect auth step for MSAL they should be directed to
    // impersonate login instead of the landing page
    if (store.getters['auth/hasImpersonateContactId']) {
      return next({ name: routeDefinitions.login.name })
    }

    // Pass the original route to the login component
    window.location.href = `${getLanguageBasedOnBaseURL()}/landing`
  }

  async function redirectToClientOverview() {
    stopRouteLoading()

    // Determine which overview page they should be taken to
    const isClientGroupOverviewEnabled = await store.dispatch(
      'features/isClientGroupOverviewEnabled'
    )

    const to = {
      name: isClientGroupOverviewEnabled
        ? routeDefinitions.clientGroupOverview.name
        : routeDefinitions.clientOverview.name,
    }

    next(to)
  }

  function redirectToErrorPage(errorPageName = 'ErrorPage') {
    stopRouteLoading()
    next({ name: errorPageName, params: [routeTo.path], replace: true })
  }

  function getErrorPageRedirectByStatusCode(response) {
    switch (response.error.source) {
      case RequestErrorSource.server: {
        const statusCode = response.error._error.response.status
        switch (statusCode) {
          case ErrorPageCodes.FORBIDDEN.statusCode:
            return redirectToErrorPage(ErrorPageCodes.FORBIDDEN.routeName)
          case ErrorPageCodes.UNAUTHORIZED.statusCode:
            return redirectToErrorPage(ErrorPageCodes.UNAUTHORIZED.routeName)
          case ErrorPageCodes.BAD_REQUEST.statusCode:
            return redirectToErrorPage(ErrorPageCodes.BAD_REQUEST.routeName)
          default:
            return redirectToErrorPage(
              ErrorPageCodes.INTERNAL_SERVER_ERROR.routeName
            )
        }
      }
      case RequestErrorSource.request:
        return redirectToErrorPage(ErrorPageCodes.NO_SERVER_RESPONSE.routeName)
      default:
        return redirectToErrorPage(
          ErrorPageCodes.ACCOUNT_LOAD_FAILURE.routeName
        )
    }
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  stopRouteLoading()
})

export default router
